import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { Grid } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydLabel from '../../../../../commonDesign/SydLabel'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import Loading from '../../../../../molecules/Loading'

const MODE_OPTIONS = [
  { value: 'restricted', label: 'Restricted (All-Accounts Only)' },
  { value: 'open', label: 'Open Access' },
  { value: 'entity', label: 'Entity-Scoped' }
]

function deriveMode (authorization) {
  if (!authorization) return 'restricted'
  return authorization.mode || 'restricted'
}

function ModifyDatasourceAuthorizationForm ({ dataSource, onComplete, onCancel }) {
  const { data: groupTypesData, isLoading: groupTypesLoading, isError: groupTypesError } = dataCatalog.useListCatalogGroupTypes()
  const { mutateAsync: modifyAuthorization } = dataCatalog.useModifyDatasourceAuthorization()

  const currentAuth = dataSource.data?.authorization || null
  const ruleIdCounter = useRef(0)
  const [mode, setMode] = useState(deriveMode(currentAuth))
  const [rules, setRules] = useState(() =>
    (currentAuth?.rules || []).map(r => ({ ...r, _id: ruleIdCounter.current++ }))
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const groupTypeOptions = useMemo(() => {
    return (groupTypesData?.data || []).map(gt => ({
      value: gt.groupTypeId,
      label: `${gt.longName} (${gt.groupTypeId})`
    }))
  }, [groupTypesData])

  const datapointOptions = useMemo(() => {
    return (dataSource.datapoints || []).map(dp => ({
      value: dp.catalogDatapointId,
      label: dp.accessor
    }))
  }, [dataSource.datapoints])

  const addRule = useCallback(() => {
    setRules(prev => [...prev, { _id: ruleIdCounter.current++, groupTypeId: null, datapointId: null }])
  }, [])

  const removeRule = useCallback((index) => {
    setRules(prev => prev.filter((_, i) => i !== index))
  }, [])

  const updateRule = useCallback((index, field, value) => {
    setRules(prev => prev.map((rule, i) =>
      i === index ? { ...rule, [field]: value } : rule
    ))
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      let authorization = null
      if (mode === 'open') {
        authorization = { mode: 'open' }
      } else if (mode === 'entity') {
        authorization = {
          mode: 'entity',
          rules: rules.filter(r => r.groupTypeId && r.datapointId).map(({ _id, ...r }) => r)
        }
      }

      await modifyAuthorization({
        datasourceId: dataSource.catalogDatasourceId,
        authorization
      })
      onComplete()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setSaving(false)
    }
  }, [mode, rules, dataSource.catalogDatasourceId, modifyAuthorization, onComplete])

  if (groupTypesLoading) return <Loading />
  if (groupTypesError) return <Alert severity='error'>Failed to load entity types. Please close and try again.</Alert>

  const isValid = mode !== 'entity' || rules.some(r => r.groupTypeId && r.datapointId)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SydLabel label='Authorization Mode'>
          <SydSelect
            options={MODE_OPTIONS}
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {mode === 'entity' && (
        <>
          {rules.map((rule, index) => (
            <Grid item xs={12} key={rule._id} container spacing={1} alignItems='flex-end'>
              <Grid item xs={5}>
                <SydLabel label='Entity Type'>
                  <SydSelect
                    options={groupTypeOptions}
                    value={rule.groupTypeId}
                    onChange={(e) => updateRule(index, 'groupTypeId', Number(e.target.value))}
                    placeholder='Select entity type...'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={5}>
                <SydLabel label='Column'>
                  <SydSelect
                    options={datapointOptions}
                    value={rule.datapointId}
                    onChange={(e) => updateRule(index, 'datapointId', Number(e.target.value))}
                    placeholder='Select column...'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={2}>
                <SydButton size='sm' variant='outline' onClick={() => removeRule(index)}>Remove</SydButton>
              </Grid>
            </Grid>
          ))}
          <Grid item xs={12}>
            <SydButton size='sm' variant='outline' icon='add' onClick={addRule}>Add Rule</SydButton>
          </Grid>
        </>
      )}

      {error && (
        <Grid item xs={12}>
          <Alert severity='error'>{error}</Alert>
        </Grid>
      )}

      <Grid item xs={12} className='flex gap-sm justify-end'>
        <SydButton variant='ghost' onClick={onCancel} disabled={saving}>Cancel</SydButton>
        <SydButton
          variant='primary'
          onClick={handleSave}
          disabled={saving || !isValid}
        >
          {saving ? 'Saving...' : 'Save'}
        </SydButton>
      </Grid>
    </Grid>
  )
}

ModifyDatasourceAuthorizationForm.propTypes = {
  dataSource: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ModifyDatasourceAuthorizationForm
